import http from "./http-common";
import { getAuthToken } from "./authService";
import { getUploadSessionId, UPLOAD_SESSION_HEADER } from "@/utils/uploadSessionId";

// Default to createdAt (immutable) so the 5 s live-poll doesn't shuffle rows every tick;
// updatedAt-desc would put whichever active package PATCHed most recently on top, which
// swaps rows every couple of seconds between two live uploads.
export const fetchActiveAdminUploads = async ({
  status,
  contributor,
  packageName,
  from,
  to,
  page = 0,
  size = 50,
  sort = "createdAt,desc",
} = {}) => {
  const token = await getAuthToken();
  const params = { page, size, sort };
  if (status && status.length > 0) {
    params.status = status.join(",");
  }
  if (contributor && contributor.trim()) {
    params.contributor = contributor.trim();
  }
  if (packageName && packageName.trim()) {
    params.packageName = packageName.trim();
  }
  // Convert local <input type=datetime-local> strings ("YYYY-MM-DDTHH:mm") to
  // full ISO for the backend @RequestParam Instant. Empty / unparseable input
  // silently drops the param rather than 400'ing server-side.
  const fromIso = toIsoOrNull(from);
  if (fromIso) params.from = fromIso;
  const toIso = toIsoOrNull(to);
  if (toIso) params.to = toIso;
  const response = await http.get("/api/v1/admin/uploads/active", {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return response.data;
};

const toIsoOrNull = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
};

export const fetchAdminUploadStats = async ({ range = "7d" } = {}) => {
  const token = await getAuthToken();
  const response = await http.get("/api/v1/admin/uploads/stats", {
    headers: { Authorization: `Bearer ${token}` },
    params: { range },
  });
  return response.data;
};

// Drill-down list for one KPI tile. `outcome` is one of UPLOADED, CANCELLED_TECHNICAL,
// CANCELLED_NON_TECHNICAL, CANCELLED_SYSTEM, FAILED — kept in sync with AdminOutcome.java.
export const fetchAdminUploadsByOutcome = async ({
  outcome,
  range = "7d",
  page = 0,
  size = 20,
  sort = "createdAt,desc",
} = {}) => {
  const token = await getAuthToken();
  const response = await http.get("/api/v1/admin/uploads/by-outcome", {
    headers: { Authorization: `Bearer ${token}` },
    params: { outcome, range, page, size, sort },
  });
  return response.data;
};

// Lazy detail for the row-expand inside the outcome drawer.
export const fetchAdminUploadDetail = async (uploadId) => {
  const token = await getAuthToken();
  const response = await http.get(`/api/v1/admin/uploads/${uploadId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Upload Observability Platform: fetches the persisted diagnostic snapshot the
// browser captured on failure / stuck detection, plus the correlated PostHog
// session id + replay URL. Powers the "Diagnostics" drawer on the admin live
// page. Returns { uploadId, contributorId, uploadSessionId, posthogSessionId,
// posthogSessionUrl, snapshotCapturedAt, snapshot } — snapshot may be null if
// none has been captured yet.
export const fetchAdminUploadDiagnostics = async (uploadId) => {
  if (!uploadId) throw new Error("uploadId is required");
  const token = await getAuthToken();
  const response = await http.get(`/api/v1/admin/uploads/${uploadId}/diagnostics`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Admin-side takeover. Calls the shared /sessions/{uploadId}/claim endpoint which the backend
// now allows admins to invoke against uploads they don't own (UploadSessionService.claim now
// bypasses the contributor check when the caller is an admin/manager and audit-logs the
// takeover). The user's client polls /sessions/active every 2 s and evicts local cards on
// ownership loss — so "upload disappears from user's session" is automatic; nothing extra to
// push. Deliberately NOT going through the packagesSlice claim thunk: that thunk rehydrates
// the claimed upload into the CURRENT session's Redux state as a PAUSED card, which is only
// meaningful for the same-user cross-device flow. An admin claiming a stranger's upload has
// no source files locally — the admin's next action is typically to cancel or investigate.
export const adminTakeOverUpload = async (uploadId) => {
  if (!uploadId) throw new Error("uploadId is required");
  const token = await getAuthToken();
  const response = await http.post(
    `/api/v1/upload/sessions/${uploadId}/claim`,
    null,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        [UPLOAD_SESSION_HEADER]: getUploadSessionId(),
      },
    }
  );
  return response.data;
};

const AdminUploadMonitorService = {
  fetchActiveAdminUploads,
  fetchAdminUploadStats,
  fetchAdminUploadsByOutcome,
  fetchAdminUploadDetail,
  fetchAdminUploadDiagnostics,
  adminTakeOverUpload,
};

export default AdminUploadMonitorService;
